import PropTypes from 'prop-types';
import { Button, Card, Image } from 'react-bootstrap';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CaseStudyPdf from './CaseStudyPdf';
import { downloadCaseStudyImage } from './caseStudyImage';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import Flex from 'components/common/Flex';
import Avatar from 'components/common/Avatar';
import SoftBadge from 'components/common/SoftBadge';
import ExpandableText from 'components/common/ExpandableText';

const CaseStudyCard = ({ caseStudy, onEdit, onDelete, showChild = true }) => {
  const [renderingImage, setRenderingImage] = useState(false);
  const handleDownloadImage = async () => {
    setRenderingImage(true);
    try {
      await downloadCaseStudyImage(caseStudy);
    } catch (e) {
      toast.error('Could not create the image');
    }
    setRenderingImage(false);
  };
  const {
    orphan,
    title,
    life_before,
    how_found_out,
    experience,
    life_now,
    quote,
    study_date,
    photos,
    branch,
    created_by_user
  } = caseStudy;
  const sections = [
    ['Life before Help Yateem', life_before],
    ['How they found out about Help Yateem', how_found_out],
    ['Their experience with Help Yateem', experience],
    ['How they are now', life_now]
  ].filter(([, text]) => text);
  const childName = orphan
    ? [orphan.first_name, orphan.middle_name, orphan.last_name]
        .filter(Boolean)
        .join(' ')
    : 'Unknown child';

  return (
    <Card className="mb-3">
      <Card.Body>
        <Flex
          justifyContent="between"
          alignItems="start"
          className="flex-wrap gap-2"
        >
          <div className="flex-1">
            <h5 className="mb-1">{title}</h5>
            <div className="fs--1 text-600 mb-2 d-flex flex-wrap gap-2 align-items-center">
              <span>{dayjs(study_date).format('DD MMM YYYY')}</span>
              {showChild && orphan && (
                <Link
                  to={`/beneficiaries/${orphan.id}`}
                  className="d-inline-flex align-items-center"
                >
                  <Avatar
                    src={orphan.image}
                    name={childName}
                    size="s"
                    className="me-1"
                  />
                  {childName}{' '}
                  <span className="text-500 ms-1">B{orphan.simple_id}</span>
                </Link>
              )}
              {branch?.name && (
                <SoftBadge pill bg="secondary">
                  {branch.name}
                </SoftBadge>
              )}
              {created_by_user && (
                <span className="text-500">
                  by {created_by_user.first_name} {created_by_user.last_name}
                </span>
              )}
            </div>
            {quote && (
              <blockquote className="border-start border-3 border-primary ps-3 fst-italic text-700 mb-2">
                “{quote}”
              </blockquote>
            )}
            {sections.map(([label, text]) => (
              <div key={label} className="mb-2">
                <div className="fs--1 fw-semi-bold text-800">{label}</div>
                <ExpandableText text={text} maxWidth={720} />
              </div>
            ))}
          </div>
          <div className="d-flex gap-2 flex-wrap">
            {/* Branded one-page card for events, sponsor updates, social posts */}
            <PDFDownloadLink
              document={<CaseStudyPdf caseStudy={caseStudy} />}
              fileName={`case-study-${(childName || 'beneficiary')
                .replace(/\s+/g, '-')
                .toLowerCase()}-${study_date || ''}.pdf`}
            >
              {({ loading }) => (
                <Button variant="falcon-success" size="sm" disabled={loading}>
                  <FontAwesomeIcon icon="download" className="me-1" />
                  {loading ? 'Preparing…' : 'Download card'}
                </Button>
              )}
            </PDFDownloadLink>
            <Button
              variant="falcon-success"
              size="sm"
              disabled={renderingImage}
              onClick={handleDownloadImage}
              title="Square 1080x1080 image for social media"
            >
              <FontAwesomeIcon icon="image" className="me-1" />
              {renderingImage ? 'Creating…' : 'Download as image'}
            </Button>
            <Button
              variant="falcon-default"
              size="sm"
              onClick={() => onEdit(caseStudy)}
            >
              Edit
            </Button>
            <Button
              variant="falcon-danger"
              size="sm"
              onClick={() => onDelete(caseStudy)}
            >
              Delete
            </Button>
          </div>
        </Flex>
        {Array.isArray(photos) && photos.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mt-3">
            {photos.map(url => (
              <a key={url} href={url} target="_blank" rel="noreferrer">
                <Image
                  src={url}
                  rounded
                  style={{ width: 120, height: 120, objectFit: 'cover' }}
                />
              </a>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

CaseStudyCard.propTypes = {
  caseStudy: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  showChild: PropTypes.bool
};

export default CaseStudyCard;
